import React, { useRef, useEffect, useMemo, useCallback } from "react";
import { motion } from "motion/react";
import { ChevronLeft, ChevronRight, Eye, Edit } from "lucide-react";
import { useContainerWidth } from "@/utils/screen";
import { useAuth } from "@/providers/AuthProvider";

interface CarouselCardItem {
  id: number | string;
  entityName?: string;
  rawData?: {
    entityName?: string;
    id?: number | string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface CarouselProps<T> {
  items: T[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  renderItem: (item: T, index: number, isActive: boolean) => React.ReactNode;
  className?: string;
  onView?: (item: T) => void;
  onEdit?: (item: T) => void;
  itemWidth?: number;
  gap?: number;
  maxVisibleItems?: number;
  parentSelector?: string; // CSS selector for matching width with another component
}

// Wrap in React.memo to prevent unnecessary rerenders
const CardCarouselComponent = React.memo(
  <T extends CarouselCardItem>({
    items,
    activeIndex,
    onActiveIndexChange,
    renderItem,
    className,
    itemWidth = 300,
    gap = 24, // Set a fixed gap in px for consistency
    maxVisibleItems = 20,
    parentSelector = ".map-container", // Default to matching the map container
  }: CarouselProps<T>) => {
    const { currentTenant } = useAuth();

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { containerWidth, ref } = useContainerWidth(parentSelector);

    // Calculate visible items based on container width
    const visibleItems = useMemo(
      () =>
        Math.min(
          maxVisibleItems,
          Math.max(1, Math.floor((containerWidth - 120) / (itemWidth + gap)))
        ),
      [containerWidth, maxVisibleItems, itemWidth, gap]
    );

    // Calculate the effective width
    const effectiveWidth = useMemo(
      () =>
        Math.min(
          containerWidth || 1200,
          visibleItems * (itemWidth + gap) - gap
        ),
      [containerWidth, visibleItems, itemWidth, gap]
    );

    // Scroll to active item
    useEffect(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const totalItemWidth = itemWidth + gap;
        const scrollPosition = totalItemWidth * activeIndex;

        container.scrollTo({
          left: scrollPosition,
          behavior: "smooth",
        });
      }
    }, [activeIndex, itemWidth, gap]);

    const handleNext = useCallback(() => {
      if (activeIndex < items.length - 1) {
        onActiveIndexChange(activeIndex + 1);
      }
    }, [activeIndex, items.length, onActiveIndexChange]);

    const handlePrev = useCallback(() => {
      if (activeIndex > 0) {
        onActiveIndexChange(activeIndex - 1);
      }
    }, [activeIndex, onActiveIndexChange]);

    // Memoize the rendered cards to prevent unnecessary rerenders
    const renderedCards = useMemo(() => {
      return items.map((item, index) => (
        <div
          key={item.id}
          className="group relative"
          style={{ width: `${itemWidth}px`, flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            // Trigger index change when the container is clicked
            onActiveIndexChange(index);
          }}
        >
          <motion.div
            className={`relative transition-transform duration-300 ease-out transform ${
              index === activeIndex ? "scale-105" : "scale-100"
            }`}
            whileHover={{ scale: index === activeIndex ? 1.05 : 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {renderItem(item, index, index === activeIndex)}
            <div className="absolute inset-0 bg-black/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

            {/* Action buttons */}
            <div className="absolute inset-0 z-[52] flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  // Navigate to details page with details tab active
                  const entityName =
                    item.entityName || item.rawData?.entityName;
                  const id = item.id;
                  if (entityName && id) {
                    window.location.href = `/org/${currentTenant?.id}/entities/${entityName}/${id}`;
                  }
                }}
                className="flex items-center px-3 py-2 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <Eye className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">View</span>
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  // Navigate to details page with edit tab active
                  const entityName =
                    item.entityName || item.rawData?.entityName;
                  const id = item.id;
                  if (entityName && id) {
                    window.location.href = `/org/${currentTenant?.id}/entities/${entityName}/${id}?tab=edit`;
                  }
                }}
                className="flex items-center px-3 py-2 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors"
              >
                <Edit className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Edit</span>
              </button>
            </div>
          </motion.div>
        </div>
      ));
    }, [items, activeIndex, itemWidth, renderItem]);

    return (
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`relative mx-auto ${className || ""}`}
        style={{ width: "100%" }}
      >
        {/* Navigation buttons - positioned further inside with proper gutters */}
        <button
          onClick={handlePrev}
          disabled={activeIndex === 0}
          className={`absolute left-2 top-1/2 -translate-y-1/2 z-[60] p-2 rounded-full bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 transition-opacity ${
            activeIndex === 0
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-100 dark:hover:bg-gray-700"
          }`}
          aria-label="Previous item"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <button
          onClick={handleNext}
          disabled={activeIndex === items.length - 1}
          className={`absolute right-4 top-1/2 -translate-y-1/2 z-[60] p-2 rounded-full bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 transition-opacity ${
            activeIndex === items.length - 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-100 dark:hover:bg-gray-700"
          }`}
          aria-label="Next item"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Carousel container */}
        <div
          className="relative px-0" // Add padding to prevent edge cards from being cut off
          style={{ maxWidth: `${effectiveWidth + 8}px`, margin: "0 auto" }}
        >
          <div
            ref={scrollContainerRef}
            className="overflow-x-scroll hide-scrollbar scroll-smooth"
          >
            <div
              className="flex px-4 py-8 min-w-min"
              style={{ gap: `${gap}px` }} // Use inline style for precise gap control
            >
              {renderedCards}
            </div>
          </div>
        </div>

        <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          height: 5px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 1ex;
        }

        .hide-scrollbar::-webkit-scrollbar-thumb {
          background: #3b82f6;
          border-radius: 1ex;
        }

        .hide-scrollbar::-webkit-scrollbar-corner {
          background: rgba(255, 255, 255, 0.2);
        }

        .scroll-smooth {
          scroll-behavior: smooth;
        }
      `}</style>
      </div>
    );
  }
);

// Set display name for React DevTools
CardCarouselComponent.displayName = "CardCarousel";

export default CardCarouselComponent;
